let cached: boolean | null = null;

/**
 * Detects whether the current platform renders ISO regional-indicator emoji as
 * actual color flags (iOS, Android, macOS) instead of the monochrome two-letter
 * fallback Windows uses — Segoe UI Emoji intentionally omits flag glyphs, so a
 * code like "AR" shows up as the stylized letters "AR" rather than a flag.
 *
 * The probe paints a flag onto an off-screen canvas and inspects the pixels: a
 * real flag produces chromatic (colored) pixels, whereas the letter fallback is
 * monochrome. The result is computed once and cached for the session.
 */
export const supportsFlagEmoji = (): boolean => {
  if (cached !== null) return cached;
  if (typeof document === 'undefined') {
    cached = false;
    return cached;
  }

  const canvas = document.createElement('canvas');
  canvas.width = 16;
  canvas.height = 16;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) {
    cached = false;
    return cached;
  }

  ctx.textBaseline = 'top';
  ctx.font = '16px sans-serif';
  // France's flag (vertical blue/white/red) guarantees strongly chromatic
  // pixels whenever the OS draws a real flag.
  ctx.fillText('\u{1F1EB}\u{1F1F7}', 0, 0);

  const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] === 0) continue; // skip transparent pixels
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    // A chromatic pixel (channels far apart) means a flag rendered; the
    // monochrome letter fallback keeps r ≈ g ≈ b.
    if (Math.max(r, g, b) - Math.min(r, g, b) > 24) {
      cached = true;
      return cached;
    }
  }

  cached = false;
  return cached;
};
